import { styled, SxProps, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import MuiStepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

// Figma: https://www.figma.com/design/UYfeNQS1UtIqy59WQyZ1CJ/EcoFutures-UI-development--Copy-?node-id=3002-3562
// Canonical stepper design shared by Scan (land upload), LandVerification and BecomeTrust/kyc.

const Connector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 13,
    left: "calc(-50% + 20px)",
    right: "calc(50% + 20px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#000",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#000",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: "#fff",
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const ERROR_RED = "#D32F2F";

const IconRoot = styled("div")<{ ownerState: { active?: boolean; completed?: boolean; error?: boolean } }>(
  ({ ownerState }) => ({
    display: "flex",
    height: 26,
    width: 26,
    boxSizing: "border-box",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: ownerState.error ? ERROR_RED : ownerState.completed ? "#000" : "white",
    border: ownerState.active && !ownerState.error ? "2px solid #000" : "none",
    color: ownerState.error ? "#fff" : ownerState.active ? "#000" : "#999",
    fontWeight: 700,
    fontSize: "12px",
    fontFamily: "'Inter', sans-serif",
    zIndex: 1,
  })
);

function StepIcon(props: StepIconProps) {
  const { active, completed, error, className, icon } = props;

  return (
    <IconRoot ownerState={{ active, completed, error }} className={className}>
      {completed && !error ? <CheckRoundedIcon sx={{ fontSize: 14, color: "#fff" }} /> : icon}
    </IconRoot>
  );
}

export interface ProgressStepperProps {
  steps: string[];
  activeStep: number;
  // Marks one step as failed — red circle, white numeral, red label. Opt-in, so the Scan,
  // LandVerification and BecomeTrust/KYC steppers that share this component are unaffected.
  errorStep?: number;
  sx?: SxProps<Theme>;
}

const ProgressStepper = ({ steps, activeStep, errorStep, sx }: ProgressStepperProps) => (
  <Box
    sx={{
      backgroundColor: "#D1EFFF",
      borderRadius: "20px",
      padding: { xs: "16px 8px", sm: "24px" },
      ...sx,
    }}
  >
    <MuiStepper activeStep={activeStep} alternativeLabel connector={<Connector />}>
      {steps.map((label, index) => (
        <Step key={label}>
          <StepLabel
            StepIconComponent={StepIcon}
            error={index === errorStep}
            sx={{
              "& .MuiStepLabel-label": {
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                letterSpacing: "0.035px",
                marginTop: "8px",
                "@media (max-width: 600px)": {
                  fontSize: "11px",
                },
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: "#000",
              },
              "& .MuiStepLabel-label.Mui-completed": {
                color: "#000",
              },
              "& .MuiStepLabel-label:not(.Mui-active):not(.Mui-completed)": {
                color: "#999",
              },
              // Last, so it outranks the three rules above whatever the step's other state is.
              "& .MuiStepLabel-label.Mui-error": {
                color: ERROR_RED,
              },
            }}
          >
            {label}
          </StepLabel>
        </Step>
      ))}
    </MuiStepper>
  </Box>
);

export default ProgressStepper;
